import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { openaiService } from '../../services/openaiService';
import PreferenceControls from './components/PreferenceControls';
import MealPlanCard from './components/MealPlanCard';
import AIInsightsPanel from './components/AIInsightsPanel';

const AINutritionPlans = () => {
  const navigate = useNavigate();
  const { user, userProfile, currentTenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mealPlans, setMealPlans] = useState([]);
  const [aiInsights, setAiInsights] = useState('');
  const [preferences, setPreferences] = useState({
    dietaryRestrictions: [],
    cuisinePreferences: [],
    caloricTarget: 2000,
    mealsPerDay: 3,
    budget: 'moderate'
  });
  const [customGoal, setCustomGoal] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (userProfile) {
      // Initialize preferences from user profile
      setPreferences(prev => ({
        ...prev,
        caloricTarget: calculateCaloricTarget(userProfile)
      }));
    }
  }, [userProfile]);

  const calculateCaloricTarget = (profile) => {
    // Basic caloric calculation based on goals
    const baseCalories = 2000;
    if (profile?.fitness_goals?.includes('weight_loss')) return baseCalories - 500;
    if (profile?.fitness_goals?.includes('muscle_gain')) return baseCalories + 500;
    return baseCalories;
  };

  const generateNutritionPlan = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const contextData = {
        userGoals: userProfile?.fitness_goals || [],
        dietaryRestrictions: preferences?.dietaryRestrictions,
        cuisinePreferences: preferences?.cuisinePreferences,
        caloricTarget: preferences?.caloricTarget,
        mealsPerDay: preferences?.mealsPerDay,
        budget: preferences?.budget,
        customGoal: customGoal,
        experienceLevel: userProfile?.experience_level,
        currentWeight: userProfile?.current_weight,
        targetWeight: userProfile?.target_weight
      };

      const prompt = `Generate a personalized daily meal plan with ${preferences?.mealsPerDay} meals totaling approximately ${preferences?.caloricTarget} calories. 

User Profile:
- Fitness Goals: ${userProfile?.fitness_goals?.join(', ') || 'general fitness'}
- Dietary Restrictions: ${preferences?.dietaryRestrictions?.join(', ') || 'none'}
- Cuisine Preferences: ${preferences?.cuisinePreferences?.join(', ') || 'any'}
- Budget: ${preferences?.budget}
${customGoal ? `- Custom Goal: ${customGoal}` : ''}

For each meal, provide:
1. Meal name and description
2. Detailed ingredients with portions
3. Nutritional breakdown (calories, protein, carbs, fats)
4. Preparation time and difficulty
5. Brief cooking instructions

Also provide an explanation of why this meal plan aligns with the user's fitness goals and how it supports their objectives.

Format the response as JSON with this structure:
{
  "meals": [
    {
      "name": "Meal Name",
      "description": "Brief description",
      "mealType": "breakfast/lunch/dinner/snack",
      "ingredients": ["ingredient 1", "ingredient 2"],
      "nutrition": {"calories": 500, "protein": 30, "carbs": 50, "fats": 15},
      "prepTime": 20,
      "difficulty": "easy/medium/hard",
      "instructions": "Step by step instructions"
    }
  ],
  "insights": "Explanation of why this plan works for the user's goals"
}`;

      const result = await openaiService?.getFitnessCoaching(
        prompt,
        'nutrition_advice',
        contextData
      );

      if (result?.success) {
        try {
          // Try to parse JSON response
          const jsonMatch = result?.response?.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsedData = JSON.parse(jsonMatch?.[0]);
            setMealPlans(parsedData?.meals || []);
            setAiInsights(parsedData?.insights || result?.response);
          } else {
            // Fallback: display raw response
            setAiInsights(result?.response);
          }
        } catch (parseError) {
          console.error('JSON parse error:', parseError);
          setAiInsights(result?.response);
        }
      } else {
        setAiInsights(`Error: ${result?.error}`);
      }
    } catch (error) {
      console.error('Error generating nutrition plan:', error);
      setAiInsights('Failed to generate nutrition plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8 lg:py-10 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Sparkles" size={24} color="var(--color-primary)" />
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
              AI Nutrition Plans
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground">
            Get personalized meal plans powered by AI, tailored to your fitness goals and dietary preferences
          </p>
        </div>

        {/* Preference Controls */}
        <PreferenceControls
          preferences={preferences}
          onPreferenceChange={handlePreferenceChange}
        />

        {/* Custom Goal Input */}
        <div className="mb-6">
          <Input
            label="Custom Goal (Optional)"
            placeholder="E.g., Prepare for marathon, Build lean muscle, Improve energy levels"
            value={customGoal}
            onChange={(e) => setCustomGoal(e?.target?.value)}
          />
        </div>

        {/* Generate Button */}
        <div className="mb-8">
          <Button
            onClick={generateNutritionPlan}
            disabled={loading}
            iconName="Sparkles"
            iconPosition="left"
            fullWidth
            className="md:w-auto md:px-8"
          >
            {loading ? 'Generating Your Plan...' : 'Generate AI Nutrition Plan'}
          </Button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-muted-foreground">AI is crafting your personalized meal plan...</p>
          </div>
        )}

        {/* Results Grid */}
        {!loading && (mealPlans?.length > 0 || aiInsights) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Meal Plans Section */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-4">
                Your Personalized Meal Plan
              </h2>
              {mealPlans?.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {mealPlans?.map((meal, index) => (
                    <MealPlanCard key={index} meal={meal} />
                  ))}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-muted-foreground whitespace-pre-wrap">{aiInsights}</p>
                </div>
              )}
            </div>

            {/* AI Insights Panel */}
            {aiInsights && mealPlans?.length > 0 && (
              <div className="lg:col-span-1">
                <AIInsightsPanel insights={aiInsights} />
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && mealPlans?.length === 0 && !aiInsights && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <Icon name="Utensils" size={32} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Ready to Create Your Nutrition Plan?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Set your preferences above and click "Generate AI Nutrition Plan" to get started with personalized meal recommendations.
            </p>
          </div>
        )}
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default AINutritionPlans;